import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Command, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const nav = useNavigate();
    const { setUser, setToken } = useAuth();

    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (!name.trim()) {
            toast.error("Please enter your name");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/accept-invite", {
                token,
                name: name.trim(),
                password,
            });
            // Store token and user
            localStorage.setItem("token", data.token);
            setToken(data.token);
            setUser(data.user);
            toast.success("Welcome to Closvio!");
            nav("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to accept invitation");
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8" data-testid="accept-invite-page">
                <div className="w-full max-w-sm text-center">
                    <h1 className="text-2xl font-display font-bold tracking-tight mb-4">Invalid Invitation</h1>
                    <p className="text-sm text-muted-foreground mb-6">
                        This invitation link is invalid or has expired.
                    </p>
                    <Link to="/login" className="text-gold font-medium hover:underline">
                        Go to login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center p-8" data-testid="accept-invite-page">
                <div className="w-full max-w-sm animate-fade-up">
                    <Link to="/" className="flex items-center gap-2 mb-10">
                        <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center rounded-md">
                            <Command className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight">Closvio</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">M&A Platform</div>
                        </div>
                    </Link>

                    <h1 className="text-3xl font-display font-bold tracking-tight mb-1">Join your team</h1>
                    <p className="text-sm text-muted-foreground mb-8">
                        You've been invited to Closvio. Set up your account to get started.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                            <Input
                                id="name"
                                type="text"
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                data-testid="invite-name-input"
                                placeholder="John Smith"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                minLength={8}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="invite-password-input"
                                placeholder="Min. 8 characters"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm" className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
                            <Input
                                id="confirm"
                                type="password"
                                required
                                minLength={8}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="invite-confirm-input"
                                placeholder="Re-enter password"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                            data-testid="invite-submit-btn"
                        >
                            {loading ? "Setting up…" : (<>Create account <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                        </Button>
                    </form>

                    <p className="text-sm text-muted-foreground mt-6">
                        Already have an account?{" "}
                        <Link to="/login" className="text-foreground font-medium underline underline-offset-4 hover:no-underline">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>

            <div className="hidden lg:block relative bg-navy overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
                    <div className="max-w-lg">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Welcome aboard</div>
                        <h2 className="font-display text-4xl font-bold tracking-tight leading-tight mb-4">
                            Your team is waiting.
                        </h2>
                        <p className="text-sm text-white/60 leading-relaxed">
                            Join your colleagues on Closvio — the deal-centric pipeline purpose-built for M&A professionals.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
